import React, { useState, useCallback, useEffect } from 'react';
import { getProductAndForms } from "../../../../api/nebProductService";
import { ClaySelect, ClayInput } from '@clayui/form';
import { faFilePdf, faFileExcel } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { exportToPdf, exportToExcel } from './exportToPdf';
import { NumericFormat } from 'react-number-format';
import { Row, Col, Card, Form, InputGroup, Modal, Button, Table, Badge, Accordion } from 'react-bootstrap';

const C1TableCheckingDP = ({ agencyId, checkingReport, totalSellouts, productAndFormsList }) => {
    const [headers, setHeaders] = useState([]);
    const [rows, setRows] = useState([]);
    const [reportBasicInfo, setReportBasicInfo] = useState(null);

    const numInputStyle = {
        textAlign: "right",
        width: "100%",
        height: "40px",
        marginRight: "5px",
        borderRadius: "5px",
        background: "#f8f8f8",
        color: "black",
    };

    const selectInputStyle = {
        width: "100%",
        height: "40px",
        marginRight: "5px",
        borderRadius: "5px",
        background: "#f8f8f8",
        color: "black",
    };

    // Initialization function
    const initialize = () => {
        // Generate headers
        const dynamicHeaders = [
            { id: "no", name: "No." },
            { id: "product", name: "Product" },
            { id: "unit", name: "Unit" },
            { id: "balance", name: "Balance" },
            ...(productAndFormsList.length > 0
                ? productAndFormsList[0].forms.map((form) => ({
                    id: form.formFieldCode,
                    name: form.formFieldName,
                }))
                : []),
            { id: "totalSellout", name: "Total Sell Out" },
        ];
        setHeaders(dynamicHeaders);
    
        const dynamicRows = productAndFormsList.map((product, index) => {
            // Assign values to product forms using checkingReport
            product.forms.forEach((form) => {
                checkingReport.forms.forEach((formCategory) => {
                    formCategory.categories.forEach((category) => {
                        category.products.forEach((fetchedProduct) => {
                            if (
                                fetchedProduct.fieldName === product.productName &&
                                category.formCode === form.formFieldCode
                            ) {
                                // Assign the fetched value to the form
                                form.value = Number(fetchedProduct.value || 0).toFixed(4);
                                product.selectedunit = fetchedProduct.unit;
                                form.productName = product.productName;
                            }
                        });
                    });
                });
            });

            // Calculate balance
            const newBalance = product.forms.reduce((formSum, form) => {
                const formBalance = Number(form.value || 0).toFixed(4); // Use the assigned value
                return formSum + parseFloat(formBalance);
            }, 0);
    
            const totalSellout = totalSellouts.find((sellout) => sellout[product.productName] !== undefined);
            const totalSelloutBalance = totalSellout ? totalSellout[product.productName] : 0;
    
            // Final calculated balance
            const finalBalance = (parseFloat(totalSelloutBalance) - newBalance).toFixed(2);
    
            return {
                no: index + 1,
                product: product.productName,
                unit: (
                    <ClaySelect
                        disabled
                        style={selectInputStyle}
                        value={product.selectedunit || "defaultUnit"} // Set the selected unit dynamically
                    >
                        {product.units.map((unit, unitIndex) => (
                            <option key={unitIndex} value={unit.productUnitName}>
                                {unit.productUnitName}
                            </option>
                        ))}
                    </ClaySelect>
                ),
                ...product.forms.reduce((acc, form) => {
                    acc[form.formFieldCode] = (
                        <NumericFormat
                            disabled
                            key={`${index}_${form.formFieldCode}`}
                            style={numInputStyle}
                            value={form.value || 0} // Use the assigned value for NumericFormat
                            thousandSeparator
                            customInput={ClayInput}
                        />
                    );
                    return acc;
                }, {}),
                totalSellout: (
                    <span
                        style={{
                            paddingRight: "10px",
                            display: "block",
                            textAlign: "right",
                        }}
                    >
                        <NumericFormat
                            displayType="text"
                            value={totalSelloutBalance.toFixed(4)} // Total Sell Out
                            thousandSeparator
                        />
                    </span>
                ),
                balance: (
                    <span
                        style={{
                            paddingRight: "10px",
                            display: "block",
                            textAlign: "right",
                        }}
                    >
                        <NumericFormat
                            displayType="text"
                            value={finalBalance} // Use the rounded final balance
                            thousandSeparator
                        />
                    </span>
                ),
            };
        });
    
        setRows(dynamicRows);
    
        // Initialize report info if available
        if (checkingReport) {
            setReportBasicInfo({
                year: checkingReport.reportPeriodYear,
                quarter: checkingReport.reportQuarter,
                agency: checkingReport.agency.agencyName,
                dataProvider: checkingReport.dataProvider.name,
                status: checkingReport.reportStatus,
                type: "Final Energy Consumption Sectors (C1)",
            });
        }
    };
    
    // Initialize on component mount
    useEffect(() => {
        initialize();
    }, [productAndFormsList, checkingReport, totalSellouts]);    

    return (
        <div>
            {/* Export to PDF */}
            <FontAwesomeIcon
                title="Download as PDF"
                style={{ cursor: 'pointer' }}
                onClick={() => exportToPdf("c1-originalunit-table", reportBasicInfo)}
                className='mr-2 text-primary'
                size='xl'
                icon={faFilePdf}
            />

            {/* Export to Excel */}
            <FontAwesomeIcon
                title="Download as Excel"
                style={{ cursor: 'pointer' }}
                onClick={() => exportToExcel("c1-originalunit-table", reportBasicInfo)}
                className='text-primary'
                size='xl'
                icon={faFileExcel}
            />

            <Table responsive bordered hover size="sm" className="mt-3" id="c1-originalunit-table">
                <thead>
                    <tr>
                        {headers.map((column) => (
                            <th
                                key={column.id}
                                className={
                                    column.id === 'no' || column.id === 'balance' || column.id === 'totalSellout' ?
                                        "table-cell-no-width"
                                        : "table-cell-minw-200"}
                                style={
                                    column.id === 'product'
                                        ? {
                                            position: 'sticky',
                                            left: 0,  // First sticky column
                                            backgroundColor: '#ECECEF',
                                            zIndex: 1,  // Ensure it's above other cells
                                        }
                                        : column.id === 'balance'
                                            ? {
                                                position: 'sticky',
                                                left: '199px',  // Adjust based on column width
                                                backgroundColor: '#ECECEF',
                                                zIndex: 1,  // Ensure it's above other cells
                                            }
                                            : {}
                                }
                            >
                                {column.name}
                            </th>
                        ))}
                    </tr>
                </thead>

                <tbody>
                    {rows.map(row => (
                        <tr key={row.no}>
                            {headers.map(column => (
                                <td
                                    key={column.id}
                                    className={
                                        column.id === 'no' || column.id === 'balance' || column.id === 'totalSellout' ?
                                            "table-cell-no-width"
                                            : "table-cell-minw-200"}
                                    style={
                                        column.id === 'product'
                                            ? {
                                                position: 'sticky',
                                                left: 0,  // First sticky column
                                                backgroundColor: '#f7f7f7',
                                                zIndex: 1,  // Ensure it's above other cells
                                            }
                                            : column.id === 'balance'
                                                ? {
                                                    position: 'sticky',
                                                    left: '199px',  // Adjust based on column width
                                                    backgroundColor: '#f7f7f7',
                                                    zIndex: 1,  // Ensure it's above other cells
                                                }
                                                : {}
                                    }
                                >
                                    {row[column.id]}
                                </td>
                            ))}
                        </tr>
                    ))}
                </tbody>
            </Table>

        </div>
    );
};

export default C1TableCheckingDP;
